import { supabase } from './supabaseClient';
import { getCurrentUser } from './auth';

/**
 * Saves a customer session to the sessions table.
 * @param {Object} sessionData - Session details
 * @returns {Promise<void>}
 * @throws {Error} If the request fails
 */
async function saveSession(sessionData) {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error('No authenticated user found');

    const { error } = await supabase.from('sessions').insert({
      barber_name: sessionData.barberName,
      services: sessionData.services,
      extras: sessionData.extras,
      items: sessionData.items,
      total_price: sessionData.totalPrice,
      date: sessionData.date || new Date().toISOString(),
      tip: sessionData.tip || 0,
      duration: sessionData.duration,
    });

    if (error) throw new Error(`Failed to save session: ${error.message}`);
  } catch (error) {
    console.error('saveSession error:', error.message);
    throw error;
  }
}

/**
 * Fetches sessions for a specific day (no user_id filter).
 * @param {string} dateKey - Date in YYYY-MM-DD format
 * @returns {Promise<Array>} List of sessions for the specified day
 * @throws {Error} If the request fails
 */
async function getDailySessions(dateKey) {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error('No authenticated user found');

    const selectedDate = dateKey ? new Date(dateKey) : new Date();
    selectedDate.setHours(0, 0, 0, 0); // Start of the selected day
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1); // Start of the next day

    const { data, error } = await supabase
      .from('sessions')
      .select('*')
      .gte('date', selectedDate.toISOString())
      .lt('date', nextDay.toISOString())
      .order('date', { ascending: false });

    if (error) throw new Error(`Failed to fetch daily sessions: ${error.message}`);
    return data || [];
  } catch (error) {
    console.error('getDailySessions error:', error.message);
    throw error;
  }
}

/**
 * Fetches all sessions (no user_id filter), optionally within a date range.
 * @param {Object} [options] - Optional filtering options
 * @param {string|Date} [options.from] - Start date (optional)
 * @param {string|Date} [options.to] - End date (optional)
 * @returns {Promise<Array>} List of all sessions
 * @throws {Error} If the request fails
 */
async function getAllSessions({ from, to } = {}) {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error('No authenticated user found');

    let query = supabase
      .from('sessions')
      .select('*')
      .order('date', { ascending: false });

    if (from) {
      const startDate = new Date(from);
      startDate.setHours(0, 0, 0, 0); // Start of day
      query = query.gte('date', startDate.toISOString());
    }

    if (to) {
      const endDate = new Date(to);
      endDate.setHours(23, 59, 59, 999); // End of day
      query = query.lte('date', endDate.toISOString());
    }

    const { data, error } = await query;

    if (error) throw new Error(`Failed to fetch all sessions: ${error.message}`);
    return data || [];
  } catch (error) {
    console.error('getAllSessions error:', error.message);
    throw error;
  }
}

/**
 * Updates an existing session in the sessions table.
 * @param {string} sessionId - The ID of the session to update
 * @param {Object} sessionData - Updated session details
 * @returns {Promise<Object>} The updated data
 * @throws {Error} If the request fails or no rows are updated
 */
async function updateSession(sessionId, sessionData) {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error('No authenticated user found');

    console.log(`Updating session ${sessionId} with data:`, JSON.stringify(sessionData, null, 2));

    // Check if the session exists
    const { data: existingData, error: fetchError } = await supabase
      .from('sessions')
      .select('id, barber_name, services, extras, items, total_price')
      .eq('id', sessionId)
      .single();

    if (fetchError) {
      console.error('Error checking session existence:', fetchError);
      throw new Error(`Failed to verify session: ${fetchError.message}`);
    }
    if (!existingData) {
      throw new Error(`Session ${sessionId} not found in the database`);
    }

    console.log(`Existing session data:`, JSON.stringify(existingData, null, 2));

    const { data, error } = await supabase
      .from('sessions')
      .update({
        barber_name: sessionData.barberName,
        services: sessionData.services,
        extras: sessionData.extras,
        items: sessionData.items,
        total_price: sessionData.totalPrice,
        tip: sessionData.tip || 0,
        duration: sessionData.duration,
      })
      .eq('id', sessionId)
      .select();

    if (error) {
      console.error('Supabase update error:', error);
      throw new Error(`Failed to update session: ${error.message}`);
    }

    if (!data || data.length === 0) {
      throw new Error(`No rows updated for session ${sessionId}. Check RLS policies or data consistency.`);
    }

    console.log(`Session ${sessionId} updated successfully. Returned data:`, JSON.stringify(data, null, 2));
    return data[0];
  } catch (error) {
    console.error('updateSession error:', error.message);
    throw error;
  }
}

/**
 * Removes a session from the sessions table.
 * @param {string} sessionId - The ID of the session to remove
 * @returns {Promise<void>}
 * @throws {Error} If the request fails
 */
async function removeSession(sessionId) {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error('No authenticated user found');

    const { error } = await supabase
      .from('sessions')
      .delete()
      .eq('id', sessionId);

    if (error) throw new Error(`Failed to remove session ${sessionId}: ${error.message}`);
  } catch (error) {
    console.error('removeSession error:', error.message);
    throw error;
  }
}

// Single export statement at the end
export { saveSession, getDailySessions, getAllSessions, updateSession, removeSession };